import React, { useEffect, useState } from "react";
import "./Clients.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { UilSearch, UilUserPlus, UilEye } from "@iconscout/react-unicons";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Link } from "react-router-dom";
import { userRequest } from "../../RequestMethods";

const STATUS_OPTIONS = ["", "prospect", "active", "inactive", "blocked"];

const StatusBadge = ({ status }) => (
  <span className={`status-badge status-${status}`}>{status}</span>
);

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: "", status: "", assignedWorkerId: "" });

  const fetchClients = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.assignedWorkerId) params.assignedWorkerId = filters.assignedWorkerId;
      const res = await userRequest.get("clients", { params });
      const all = res.data.clients || [];
      const searched = filters.search
        ? all.filter((c) =>
            c.companyName?.toLowerCase().includes(filters.search.toLowerCase()) ||
            c.email?.toLowerCase().includes(filters.search.toLowerCase())
          )
        : all;
      setClients(searched);
    } catch (err) {
      if (err?.response?.status !== 404) {
        toast.error(err?.response?.data?.message || "Erreur lors du chargement des clients.");
      } else {
        setClients([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkers = async () => {
    try {
      const res = await userRequest.get("workers");
      setWorkers(res.data.workers || res.data || []);
    } catch {
      // non-blocking
    }
  };

  useEffect(() => {
    fetchWorkers();
  }, []);

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.assignedWorkerId]);

  const handleFilterChange = (e) => {
    setFilters((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSearch = (e) => {
    if (e.key === "Enter") fetchClients();
    setFilters((prev) => ({ ...prev, search: e.target.value }));
  };

  return (
    <div className="clients-page">
      <div className="clients-header">
        <h1>Clients B2B</h1>
        <Link to="/add-client" className="clients-add-btn">
          <UilUserPlus size={18} />
          Nv Client
        </Link>
      </div>

      <div className="clients-toolbar">
        <div className="clients-search">
          <UilSearch size={16} />
          <input
            name="search"
            placeholder="Rechercher par nom, email..."
            value={filters.search}
            onChange={handleSearch}
            onKeyDown={handleSearch}
          />
        </div>
        <select className="clients-filter" name="status" value={filters.status} onChange={handleFilterChange}>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s || "Tous les statuts"}</option>
          ))}
        </select>
        <select className="clients-filter" name="assignedWorkerId" value={filters.assignedWorkerId} onChange={handleFilterChange}>
          <option value="">Tous les commerciaux</option>
          {workers.map((w) => (
            <option key={w._id} value={w._id}>{w.name} {w.surname}</option>
          ))}
        </select>
      </div>

      <div className="clients-count">{clients.length} client{clients.length !== 1 ? "s" : ""}</div>

      {loading ? (
        <div className="clients-loading">Chargement...</div>
      ) : (
        <TableContainer component={Paper} className="clients-table-container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Entreprise</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Téléphone</TableCell>
                <TableCell>Magasins</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Commercial</TableCell>
                <TableCell>Tags</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" style={{ padding: "2rem", color: "#9ca3af" }}>
                    Aucun client trouvé
                  </TableCell>
                </TableRow>
              ) : (
                clients.map((client) => (
                  <TableRow key={client._id} hover>
                    <TableCell>
                      <div className="client-company">
                        <span className="client-avatar">
                          {(client.companyName || "?")[0].toUpperCase()}
                        </span>
                        <div>
                          <div className="client-name">{client.companyName}</div>
                          {client.siret && (
                            <div className="client-siret">SIRET {client.siret}</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>{client.email}</TableCell>
                    <TableCell>{client.number || "—"}</TableCell>
                    <TableCell>{client.magasins?.length || 0}</TableCell>
                    <TableCell>
                      {client.status ? <StatusBadge status={client.status} /> : "—"}
                    </TableCell>
                    <TableCell>
                      {client.assignedWorkerId
                        ? `${client.assignedWorkerId.name} ${client.assignedWorkerId.surname}`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {client.tags?.length > 0
                        ? client.tags.map((t) => (
                            <span key={t} className="status-badge status-inactive" style={{ marginRight: 4 }}>{t}</span>
                          ))
                        : "—"}
                    </TableCell>
                    <TableCell align="center">
                      <Link to={`/clients/${client._id}`} className="client-action-btn">
                        <UilEye size={16} />
                      </Link>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ToastContainer position="top-right" />
    </div>
  );
};

export default Clients;
